import React, { useEffect, useState } from 'react';
import { XIcon, Loader2Icon, HistoryIcon, RotateCcwIcon, ClockIcon, UserIcon, TagIcon, FileTextIcon, CheckCircleIcon, EyeIcon } from 'lucide-react';
import { formVersionsApi, emailTemplateVersionsApi, FormVersionRecord, EmailTemplateVersionRecord } from '../services/creatorApi';
import { ConfirmModal } from './ConfirmModal';
import { toast } from 'sonner';
type VersionRecord = FormVersionRecord | EmailTemplateVersionRecord;
interface VersionHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'form' | 'email';
  entityId: string;
  entityName: string;
  onRestore: (version: VersionRecord) => Promise<void>;
}
/**
 * Parse Zoho Creator's Added_Time format reliably.
 * Creator returns dates like "15-Mar-2026 22:45:31" (dd-MMM-yyyy HH:mm:ss)
 * which `new Date()` can't always parse across browsers.
 */
const MONTH_MAP: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11
};
function parseCreatorDate(dateStr?: string): Date | null {
  if (!dateStr) return null;
  const native = new Date(dateStr);
  if (!isNaN(native.getTime())) return native;
  const match = dateStr.match(/^(\d{1,2})-(\w{3})-(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})/);
  if (match) {
    const [, day, mon, year, hour, min, sec] = match;
    const monthIdx = MONTH_MAP[mon.toLowerCase()];
    if (monthIdx !== undefined) {
      return new Date(+year, monthIdx, +day, +hour, +min, +sec);
    }
  }
  return null;
}
function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  try {
    const d = parseCreatorDate(dateStr);
    if (!d) return dateStr;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
}
function getRelativeTime(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const d = parseCreatorDate(dateStr);
    if (!d) return '';
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return '';
  } catch {
    return '';
  }
}
/** Get the HTML content from a version record for preview */
function getVersionHtml(version: VersionRecord, entityType: 'form' | 'email'): string {
  if (entityType === 'email') {
    return (version as EmailTemplateVersionRecord).Email_HTML_Body || '';
  }
  const formVersion = version as FormVersionRecord;
  const html = formVersion.HTML || '';
  const css = formVersion.CSS || '';
  if (css) {
    return html + '<style>{`' + css + '`}</style>';
  }
  return html;
}
export function VersionHistoryPanel({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityName,
  onRestore
}: VersionHistoryPanelProps) {
  const [versions, setVersions] = useState<VersionRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<VersionRecord | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<VersionRecord | null>(null);
  useEffect(() => {
    if (!isOpen || !entityId) return;
    setIsLoading(true);
    setError(null);
    const fetchVersions = async () => {
      try {
        let data: VersionRecord[];
        if (entityType === 'form') {
          data = await formVersionsApi.listByFormId(entityId);
        } else {
          data = await emailTemplateVersionsApi.listByTemplateId(entityId);
        }
        // Sort: active version first, then by version number descending
        data.sort((a, b) => {
          // Active version always comes first
          const aActive = a.Is_Active === true;
          const bActive = b.Is_Active === true;
          if (aActive && !bActive) return -1;
          if (!aActive && bActive) return 1;
          // Then by version number descending
          const numDiff = (b.Version_Number || 0) - (a.Version_Number || 0);
          if (numDiff !== 0) return numDiff;
          // Then by Added_Time as tiebreaker
          const dateA = parseCreatorDate(a.Added_Time);
          const dateB = parseCreatorDate(b.Added_Time);
          if (dateA && dateB) return dateB.getTime() - dateA.getTime();
          return 0;
        });
        setVersions(data);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to load versions';
        setError(msg);
      } finally {
        setIsLoading(false);
      }
    };
    fetchVersions();
  }, [isOpen, entityId, entityType]);
  const handleRestoreConfirm = async () => {
    if (!restoreTarget) return;
    setIsRestoring(true);
    try {
      // 1. Deactivate ALL versions that are currently active (uncheck Is_Active for every version except the target)
      const activeVersions = versions.filter(v => v.Is_Active === true && v.ID !== restoreTarget.ID);
      for (const activeVersion of activeVersions) {
        try {
          if (entityType === 'form') {
            await formVersionsApi.update(activeVersion.ID, {
              Is_Active: false
            });
          } else {
            await emailTemplateVersionsApi.update(activeVersion.ID, {
              Is_Active: false
            });
          }
        } catch (err) {
          console.warn('Failed to deactivate version', activeVersion.ID, ':', err);
        }
      }
      // 2. Mark the restored version as active
      try {
        if (entityType === 'form') {
          await formVersionsApi.update(restoreTarget.ID, {
            Is_Active: true
          });
        } else {
          await emailTemplateVersionsApi.update(restoreTarget.ID, {
            Is_Active: true
          });
        }
      } catch (err) {
        console.warn('Failed to mark restored version as active:', err);
      }
      // 3. Copy the version content to the main record
      await onRestore(restoreTarget);
      // 4. Update local state to reflect the change
      setVersions(prev => prev.map(v => ({
        ...v,
        Is_Active: v.ID === restoreTarget.ID ? true : false
      })).sort((a, b) => {
        const aActive = a.Is_Active === true;
        const bActive = b.Is_Active === true;
        if (aActive && !bActive) return -1;
        if (!aActive && bActive) return 1;
        return (b.Version_Number || 0) - (a.Version_Number || 0);
      }));
      toast.success(`Restored to Version ${restoreTarget.Version_Number}`, {
        description: restoreTarget.Version_Label ? `"${restoreTarget.Version_Label}"` : `${entityName} has been restored.`
      });
      setRestoreTarget(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Restore failed';
      toast.error('Failed to restore version', {
        description: msg
      });
    } finally {
      setIsRestoring(false);
    }
  };
  if (!isOpen) return null;
  // Determine which version is active
  // If no version has Is_Active set, fall back to the highest version number
  const hasActiveFlag = versions.some(v => v.Is_Active === true);
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30 transition-opacity" onClick={onClose} data-id="element-2194" />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col animate-slide-in-right" data-id="element-2195">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50/80" data-id="element-2196">
          <div className="flex items-center gap-3" data-id="element-2197">
            <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center" data-id="element-2198">
              <HistoryIcon className="h-5 w-5 text-indigo-600" data-id="element-2199" />
            </div>
            <div data-id="element-2200">
              <h2 className="text-base font-semibold text-gray-900" data-id="element-2201">
                Version History
              </h2>
              <p className="text-xs text-gray-500 truncate max-w-[240px]" data-id="element-2202">
                {entityName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-2203">
            <XIcon className="h-5 w-5" data-id="element-2204" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" data-id="element-2205">
          {isLoading && <div className="flex flex-col items-center justify-center py-20 text-gray-400" data-id="element-2206">
              <Loader2Icon className="h-8 w-8 animate-spin mb-3" data-id="element-2207" />
              <p className="text-sm" data-id="element-2208">Loading versions…</p>
            </div>}

          {error && !isLoading && <div className="p-5" data-id="element-2209">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700" data-id="element-2210">
                {error}
              </div>
            </div>}

          {!isLoading && !error && versions.length === 0 && <div className="flex flex-col items-center justify-center py-20 text-gray-400" data-id="element-2211">
              <HistoryIcon className="h-10 w-10 mb-3 opacity-40" data-id="element-2212" />
              <p className="text-sm font-medium text-gray-500" data-id="element-2213">
                No versions yet
              </p>
              <p className="text-xs text-gray-400 mt-1 text-center max-w-[240px]" data-id="element-2214">
                Versions are created each time you save. Save your{' '}
                {entityType === 'form' ? 'form' : 'template'} to start tracking
                history.
              </p>
            </div>}

          {!isLoading && !error && versions.length > 0 && <div className="py-2" data-id="element-2215">
              {/* Version count */}
              <div className="px-5 py-2" data-id="element-2216">
                <p className="text-xs text-gray-500" data-id="element-2217">
                  {versions.length} version{versions.length !== 1 ? 's' : ''}{' '}
                  found
                </p>
              </div>

              {/* Version list */}
              <div className="space-y-0" data-id="element-2218">
                {versions.map((version, idx) => {
              // A version is "active" if Is_Active === 'true', or if no active flags exist, the highest version number
              const isActive = hasActiveFlag ? version.Is_Active === true : idx === 0;
              const versionNum = version.Version_Number;
              const label = version.Version_Label;
              const author = version.Created_By_Name || version.Created_By_Email || 'Unknown';
              const date = version.Added_Time;
              const summary = version.Change_Summary;
              const relTime = getRelativeTime(date);
              return <div key={version.ID} className={`relative px-5 py-4 border-b border-gray-100 transition-colors ${isActive ? 'bg-indigo-50/40' : 'hover:bg-gray-50/60'}`} data-id="element-2219">
                      {/* Timeline connector */}
                      <div className="absolute left-7 top-0 bottom-0 w-px bg-gray-200" data-id="element-2220" />
                      {idx === versions.length - 1 && <div className="absolute left-7 bottom-0 h-1/2 w-px bg-white" data-id="element-2221" />}
                      {idx === 0 && <div className="absolute left-7 top-0 h-4 w-px bg-white" data-id="element-2222" />}

                      <div className="flex items-start gap-3 ml-4" data-id="element-2223">
                        {/* Timeline dot */}
                        <div className={`relative z-10 flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold -ml-7 ${isActive ? 'bg-indigo-600 text-white ring-2 ring-indigo-200' : 'bg-white border-2 border-gray-300 text-gray-500'}`} data-id="element-2224">
                          {isActive ? <CheckCircleIcon className="h-3.5 w-3.5" data-id="element-2225" /> : <span className="text-[10px]" data-id="element-2226">{versionNum}</span>}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0" data-id="element-2227">
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-2228">
                            <span className="text-sm font-semibold text-gray-900" data-id="element-2229">
                              Version {versionNum}
                            </span>
                            {isActive && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700 uppercase tracking-wide" data-id="element-2230">
                                Active
                              </span>}
                            {label && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600" data-id="element-2231">
                                <TagIcon className="h-2.5 w-2.5" data-id="element-2232" />
                                {label}
                              </span>}
                          </div>

                          {summary && <p className="text-xs text-gray-600 mt-1 line-clamp-2" data-id="element-2233">
                              <FileTextIcon className="h-3 w-3 inline mr-1 text-gray-400 -mt-0.5" data-id="element-2234" />
                              {summary}
                            </p>}

                          <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-400" data-id="element-2235">
                            <span className="inline-flex items-center gap-1" data-id="element-2236">
                              <UserIcon className="h-3 w-3" data-id="element-2237" />
                              {author}
                            </span>
                            <span className="inline-flex items-center gap-1" data-id="element-2238">
                              <ClockIcon className="h-3 w-3" data-id="element-2239" />
                              {formatDate(date)}
                              {relTime && <span className="text-gray-300 ml-0.5" data-id="element-2240">
                                  ({relTime})
                                </span>}
                            </span>
                          </div>

                          {/* Action buttons */}
                          <div className="flex items-center gap-2 mt-2.5" data-id="element-2241">
                            {/* Preview button — shown on all versions */}
                            <button onClick={() => setPreviewVersion(version)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 hover:border-gray-300 transition-colors" data-id="element-2242">
                              <EyeIcon className="h-3 w-3" data-id="element-2243" />
                              Preview
                            </button>

                            {/* Restore button — not shown for the active version */}
                            {!isActive && <button onClick={() => setRestoreTarget(version)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 hover:border-indigo-300 transition-colors" data-id="element-2244">
                                <RotateCcwIcon className="h-3 w-3" data-id="element-2245" />
                                Restore
                              </button>}
                          </div>
                        </div>
                      </div>
                    </div>;
            })}
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50/80" data-id="element-2246">
          <p className="text-[11px] text-gray-400 text-center" data-id="element-2247">
            Restoring a version copies its content to the main{' '}
            {entityType === 'form' ? 'form' : 'template'} record. All version
            history entries are preserved.
          </p>
        </div>
      </div>

      {/* Preview Modal */}
      {previewVersion && <div className="fixed inset-0 z-[60] overflow-y-auto" data-id="element-2248">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-2249">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setPreviewVersion(null)} data-id="element-2250" />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" data-id="element-2251">
              {/* Preview Header */}
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 bg-gray-50/80 flex-shrink-0" data-id="element-2252">
                <div className="flex items-center gap-3 min-w-0" data-id="element-2253">
                  <div className="h-7 w-7 rounded-md bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-2254">
                    <EyeIcon className="h-4 w-4 text-indigo-600" data-id="element-2255" />
                  </div>
                  <div className="min-w-0" data-id="element-2256">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-2257">
                      Version {previewVersion.Version_Number} Preview
                    </h3>
                    <p className="text-[11px] text-gray-500 truncate" data-id="element-2258">
                      {previewVersion.Change_Summary || entityName}
                      {previewVersion.Is_Active === true && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-green-100 text-green-700 uppercase" data-id="element-2259">
                          Active
                        </span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0" data-id="element-2260">
                  {/* Restore from preview — only if not the active version */}
                  {previewVersion.Is_Active !== true && !(!hasActiveFlag && versions.indexOf(previewVersion) === 0) && <button onClick={() => {
                setPreviewVersion(null);
                setRestoreTarget(previewVersion);
              }} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors" data-id="element-2261">
                        <RotateCcwIcon className="h-3 w-3" data-id="element-2262" />
                        Restore
                      </button>}
                  <button onClick={() => setPreviewVersion(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" data-id="element-2263">
                    <XIcon className="h-4 w-4" data-id="element-2264" />
                  </button>
                </div>
              </div>

              {/* Preview Content */}
              <div className="flex-1 overflow-hidden bg-gray-100" data-id="element-2265">
                {getVersionHtml(previewVersion, entityType) ? <iframe srcDoc={getVersionHtml(previewVersion, entityType)} title={`Version ${previewVersion.Version_Number} Preview`} className="w-full h-full min-h-[400px] border-0 bg-white" sandbox="allow-same-origin" data-id="element-2266" /> : <div className="flex flex-col items-center justify-center py-20 text-gray-400" data-id="element-2267">
                    <FileTextIcon className="h-10 w-10 mb-3 opacity-40" data-id="element-2268" />
                    <p className="text-sm font-medium text-gray-500" data-id="element-2269">
                      No content to preview
                    </p>
                    <p className="text-xs text-gray-400 mt-1" data-id="element-2270">
                      This version has no HTML content saved.
                    </p>
                  </div>}
              </div>

              {/* Preview Footer */}
              <div className="px-5 py-3 border-t border-gray-200 bg-gray-50/80 flex items-center justify-between flex-shrink-0" data-id="element-2271">
                <div className="text-[11px] text-gray-400" data-id="element-2272">
                  <span className="inline-flex items-center gap-1" data-id="element-2273">
                    <UserIcon className="h-3 w-3" data-id="element-2274" />
                    {previewVersion.Created_By_Name || previewVersion.Created_By_Email || 'Unknown'}
                  </span>
                  <span className="mx-2" data-id="element-2275">•</span>
                  <span className="inline-flex items-center gap-1" data-id="element-2276">
                    <ClockIcon className="h-3 w-3" data-id="element-2277" />
                    {formatDate(previewVersion.Added_Time)}
                  </span>
                </div>
                <button onClick={() => setPreviewVersion(null)} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-2278">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Confirm Restore Modal */}
      <ConfirmModal isOpen={!!restoreTarget} title="Restore Version" message={restoreTarget ? `Are you sure you want to restore "${entityName}" to Version ${restoreTarget.Version_Number}${restoreTarget.Version_Label ? ` ("${restoreTarget.Version_Label}")` : ''}? This will copy Version ${restoreTarget.Version_Number}'s content to the main ${entityType === 'form' ? 'form' : 'template'} record and mark it as the active version. All version history entries will be preserved.` : ''} confirmLabel={isRestoring ? 'Restoring…' : 'Yes, Restore'} cancelLabel="Cancel" onConfirm={handleRestoreConfirm} onCancel={() => setRestoreTarget(null)} variant="warning" data-id="element-2279" />
    </>;
}